import React, { useState, useEffect } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Plus, AlertTriangle, Settings, 
  Trash2, Edit, X, Wrench
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CORES_DISPONIVEIS = [
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#f97316', label: 'Laranja' },
  { value: '#eab308', label: 'Amarelo' },
  { value: '#22c55e', label: 'Verde' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#6b7280', label: 'Cinza' }
];

// Tipos de classificação de anomalia
const CLASSIFICACAO_ANOMALIA = [
  { id: 'defeito', label: 'Defeito (Perda Total)', description: 'Peça não pode ser recuperada', icon: X, color: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'arranjo', label: 'Para Arranjo', description: 'Peça pode ser recuperada', icon: Wrench, color: 'bg-amber-100 text-amber-700 border-amber-300' }
];

export default function GestaoAnomalias() {
  const [tiposAnomalia, setTiposAnomalia] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog para criar/editar tipo
  const [showTipoDialog, setShowTipoDialog] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [tipoForm, setTipoForm] = useState({
    nome: '',
    codigo: '',
    cor: '#ef4444',
    descricao: '',
    classificacao: 'defeito' // 'defeito' ou 'arranjo'
  });
  
  // Dialog de confirmação de eliminação
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tipoToDelete, setTipoToDelete] = useState(null);

  useEffect(() => {
    loadTiposAnomalia();
  }, []);

  const loadTiposAnomalia = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tipos-anomalia`);
      setTiposAnomalia(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao carregar tipos de anomalia'));
    } finally {
      setLoading(false);
    }
  };

  const resetTipoForm = () => {
    setTipoForm({
      nome: '',
      codigo: '',
      cor: '#ef4444',
      descricao: '',
      classificacao: 'defeito'
    });
    setEditingTipo(null);
  };

  const handleSaveTipo = async () => {
    try {
      if (!tipoForm.nome.trim()) {
        toast.error('O nome do tipo é obrigatório');
        return;
      }
      if (!tipoForm.codigo.trim()) {
        toast.error('O código do tipo é obrigatório');
        return;
      }

      if (editingTipo) {
        await axios.put(`${API}/tipos-anomalia/${editingTipo.id}`, tipoForm);
        toast.success('Tipo de anomalia atualizado');
      } else {
        await axios.post(`${API}/tipos-anomalia`, tipoForm);
        toast.success('Tipo de anomalia criado');
      }
      
      setShowTipoDialog(false);
      resetTipoForm();
      loadTiposAnomalia();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao guardar tipo de anomalia'));
    }
  };

  const openEditTipo = (tipo) => {
    setEditingTipo(tipo);
    setTipoForm({
      nome: tipo.nome,
      codigo: tipo.codigo,
      cor: tipo.cor,
      descricao: tipo.descricao || '',
      classificacao: tipo.classificacao || 'defeito'
    });
    setShowTipoDialog(true);
  };

  const handleDeleteTipo = async () => {
    if (!tipoToDelete) return;
    
    try {
      await axios.delete(`${API}/tipos-anomalia/${tipoToDelete.id}`);
      toast.success('Tipo de anomalia eliminado');
      setShowDeleteDialog(false);
      setTipoToDelete(null);
      loadTiposAnomalia();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao eliminar tipo de anomalia'));
    }
  };

  // Separar tipos por classificação
  const tiposDefeito = tiposAnomalia.filter(t => t.classificacao === 'defeito' || !t.classificacao);
  const tiposArranjo = tiposAnomalia.filter(t => t.classificacao === 'arranjo');

  return (
    <GestorLayout>
      <div className="max-w-4xl mx-auto">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading font-bold text-stone-900 flex items-center gap-3">
              <AlertTriangle className="h-7 w-7 text-primary" />
              Tipos de Anomalias
            </h1>
            <p className="text-stone-500 mt-1">
              Configure os tipos de anomalias que os operadores podem reportar
            </p>
          </div>
          <Button 
            onClick={() => { resetTipoForm(); setShowTipoDialog(true); }} 
            data-testid="btn-novo-tipo"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Tipo
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : tiposAnomalia.length === 0 ? (
          <Card className="p-12 text-center">
            <Settings className="h-16 w-16 mx-auto text-stone-300 mb-4" />
            <p className="text-xl font-medium text-stone-700 mb-2">Nenhum tipo configurado</p>
            <p className="text-stone-500 mb-6">
              Crie tipos de anomalias para que os operadores possam reportar problemas durante a produção
            </p>
            <Button onClick={() => setShowTipoDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Primeiro Tipo
            </Button>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Defeitos */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <X className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-stone-900">Defeitos (Perda Total)</h2>
                  <p className="text-sm text-stone-500">Peças que não podem ser recuperadas</p>
                </div>
              </div>
              
              {tiposDefeito.length === 0 ? (
                <Card className="p-6 text-center border-dashed">
                  <p className="text-stone-400">Nenhum tipo de defeito configurado</p>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {tiposDefeito.map((tipo) => (
                    <Card key={tipo.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: tipo.cor }}
                          >
                            {tipo.codigo}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{tipo.nome}</p>
                            {tipo.descricao && (
                              <p className="text-sm text-stone-500">{tipo.descricao}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditTipo(tipo)}
                            data-testid={`edit-tipo-${tipo.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => { setTipoToDelete(tipo); setShowDeleteDialog(true); }}
                            data-testid={`delete-tipo-${tipo.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Para Arranjo */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-stone-900">Para Arranjo</h2>
                  <p className="text-sm text-stone-500">Peças que podem ser recuperadas</p>
                </div>
              </div>
              
              {tiposArranjo.length === 0 ? (
                <Card className="p-6 text-center border-dashed">
                  <p className="text-stone-400">Nenhum tipo de arranjo configurado</p>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {tiposArranjo.map((tipo) => (
                    <Card key={tipo.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: tipo.cor }}
                          >
                            {tipo.codigo}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{tipo.nome}</p>
                            {tipo.descricao && (
                              <p className="text-sm text-stone-500">{tipo.descricao}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditTipo(tipo)}
                            data-testid={`edit-tipo-${tipo.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => { setTipoToDelete(tipo); setShowDeleteDialog(true); }}
                            data-testid={`delete-tipo-${tipo.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dialog Criar/Editar Tipo */}
        <Dialog open={showTipoDialog} onOpenChange={(open) => { setShowTipoDialog(open); if (!open) resetTipoForm(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingTipo ? 'Editar Tipo de Anomalia' : 'Novo Tipo de Anomalia'}
              </DialogTitle>
              <DialogDescription>
                Configure um tipo de anomalia que os operadores podem reportar
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Classificação */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Classificação *</label>
                <div className="grid grid-cols-2 gap-3">
                  {CLASSIFICACAO_ANOMALIA.map((classif) => {
                    const Icon = classif.icon;
                    return (
                      <button
                        key={classif.id}
                        type="button"
                        onClick={() => setTipoForm(prev => ({ ...prev, classificacao: classif.id }))}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          tipoForm.classificacao === classif.id 
                            ? `${classif.color} border-current` 
                            : 'bg-white border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-5 w-5" />
                          <span className="font-medium">{classif.label}</span>
                        </div>
                        <p className="text-xs opacity-70">{classif.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nome *</label>
                <Input
                  placeholder="Ex: Material defeituoso"
                  value={tipoForm.nome}
                  onChange={(e) => setTipoForm(prev => ({ ...prev, nome: e.target.value }))}
                  data-testid="input-tipo-nome"
                />
              </div>
              
              {/* Código */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Código *</label>
                <Input
                  placeholder="Ex: DEF01"
                  value={tipoForm.codigo}
                  onChange={(e) => setTipoForm(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                  maxLength={6}
                  data-testid="input-tipo-codigo"
                />
                <p className="text-xs text-stone-400 mt-1">Código curto para identificação rápida</p>
              </div>
              
              {/* Cor */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Cor</label>
                <div className="flex flex-wrap gap-2">
                  {CORES_DISPONIVEIS.map((cor) => (
                    <button
                      key={cor.value}
                      type="button"
                      onClick={() => setTipoForm(prev => ({ ...prev, cor: cor.value }))}
                      className={`w-8 h-8 rounded-lg transition-all ${
                        tipoForm.cor === cor.value ? 'ring-2 ring-offset-2 ring-stone-400 scale-110' : ''
                      }`}
                      style={{ backgroundColor: cor.value }}
                      title={cor.label}
                    />
                  ))}
                </div>
              </div>
              
              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Descrição (opcional)</label>
                <Textarea
                  placeholder="Descreva quando este tipo deve ser usado..."
                  value={tipoForm.descricao}
                  onChange={(e) => setTipoForm(prev => ({ ...prev, descricao: e.target.value }))}
                  rows={2}
                  data-testid="input-tipo-descricao"
                />
              </div>
              
              {/* Pré-visualização */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-xs text-stone-500 mb-2">Pré-visualização:</p>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: tipoForm.cor }}
                  >
                    {tipoForm.codigo || '???'}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{tipoForm.nome || 'Nome do tipo'}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      tipoForm.classificacao === 'arranjo' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {tipoForm.classificacao === 'arranjo' ? 'Para Arranjo' : 'Defeito'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowTipoDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveTipo} data-testid="btn-save-tipo">
                {editingTipo ? 'Guardar Alterações' : 'Criar Tipo'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Confirmar Eliminação */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-red-600">Eliminar Tipo de Anomalia</DialogTitle>
              <DialogDescription>
                Tem a certeza que deseja eliminar o tipo "{tipoToDelete?.nome}"? 
                Esta ação não pode ser revertida.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteTipo}
                data-testid="btn-confirm-delete"
              >
                Eliminar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </GestorLayout>
  );
}
